import { Rng } from './Rng.ts';

/**
 * Parallax scenery, drawn in art-pixel coordinates.
 *
 * All of it is cosmetic and none of it touches the simulation. Two properties
 * are still worth keeping:
 *
 *   - Every scroll offset is derived from `simTimeMs`, not from the wall clock,
 *     so a replay looks frame-for-frame identical to the run it replays.
 *   - The cloud and bush layout comes from a fixed seed rather than
 *     `Math.random()`, which is banned project-wide.
 *
 * Everything is drawn on whole art pixels. A fractional fillRect would be
 * antialiased by the browser and the 8-bit look would dissolve into mush.
 */

export const COLORS = {
  skyTop: '#4ec0ca',
  skyBottom: '#8ce0e4',
  cloud: '#ffffff',
  cloudShade: '#dff4f6',
  bush: '#5ac846',
  bushDark: '#3f9c31',
  bushEdge: '#2c7a22',

  pipe: '#74bf2e',
  pipeLight: '#a8e05a',
  pipeDark: '#4e7a1e',
  pipeOutline: '#20401a',

  grass: '#74c02e',
  grassDark: '#4e8a1e',
  sand: '#ded895',
  sandDark: '#c8bd6f',
  soil: '#a89050',
} as const;

/** Height of the ground strip, in art pixels. It sits *below* the death line. */
export const GROUND_ART_HEIGHT = 28;

interface Blob {
  x: number;
  y: number;
  w: number;
  h: number;
}

export class Backdrop {
  private clouds: Blob[] = [];
  private bushes: Blob[] = [];

  /** Width of one repeat of each parallax layer, art px. */
  private readonly cloudSpan: number;
  private readonly bushSpan: number;

  constructor(
    private readonly artWidth: number,
    fieldHeight: number,
  ) {
    // One repeat is two screens wide, so the seam is never on screen twice.
    this.cloudSpan = artWidth * 2;
    this.bushSpan = artWidth * 2;

    const rng = new Rng(0xc10d5);
    for (let x = 0; x < this.cloudSpan; x += rng.int(28, 52)) {
      this.clouds.push({
        x,
        y: rng.int(14, Math.max(16, Math.floor(fieldHeight * 0.42))),
        w: rng.int(18, 34),
        h: rng.int(6, 10),
      });
    }
    // Deliberately short and overlapping: tall scenery inside the play field
    // reads as an obstacle, and these are not obstacles.
    for (let x = 0; x < this.bushSpan; x += rng.int(9, 16)) {
      this.bushes.push({ x, y: 0, w: rng.int(18, 32), h: rng.int(7, 13) });
    }
  }

  /** Flat-ish sky with a soft vertical ramp, quantised into bands. */
  drawSky(ctx: CanvasRenderingContext2D, height: number): void {
    // A real gradient would dither across hundreds of levels; six hard bands
    // read as 8-bit and cost nothing.
    const bands = 6;
    const top = hexToRgb(COLORS.skyTop);
    const bottom = hexToRgb(COLORS.skyBottom);
    for (let i = 0; i < bands; i++) {
      const t = i / (bands - 1);
      ctx.fillStyle = `rgb(${mix(top[0], bottom[0], t)},${mix(top[1], bottom[1], t)},${mix(top[2], bottom[2], t)})`;
      const y0 = Math.floor((i / bands) * height);
      const y1 = Math.floor(((i + 1) / bands) * height);
      ctx.fillRect(0, y0, this.artWidth, y1 - y0);
    }
  }

  /** Clouds drift at a fraction of the pipe speed. */
  drawClouds(ctx: CanvasRenderingContext2D, simTimeMs: number, pipeSpeed: number): void {
    const offset = scrollOffset(simTimeMs, pipeSpeed * 0.12, this.cloudSpan);
    for (const c of this.clouds) {
      for (const wrap of [0, this.cloudSpan]) {
        const x = Math.round(c.x - offset + wrap);
        if (x > this.artWidth || x + c.w < 0) continue;
        this.drawCloud(ctx, x, c.y, c.w, c.h);
      }
    }
  }

  private drawCloud(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    // Three stacked rows of decreasing width read as a rounded puff without a
    // single curved edge.
    ctx.fillStyle = COLORS.cloud;
    ctx.fillRect(x, y + h - 3, w, 3);
    ctx.fillRect(x + 3, y + 2, w - 6, h - 4);
    ctx.fillRect(x + Math.floor(w * 0.3), y, Math.ceil(w * 0.4), 3);
    ctx.fillStyle = COLORS.cloudShade;
    ctx.fillRect(x + 2, y + h - 2, w - 4, 2);
  }

  /** A band of bushes along the horizon, above the ground. */
  drawBushes(
    ctx: CanvasRenderingContext2D,
    simTimeMs: number,
    pipeSpeed: number,
    baseY: number,
  ): void {
    const offset = scrollOffset(simTimeMs, pipeSpeed * 0.35, this.bushSpan);

    // Two passes over the whole band rather than per-bush shading. Shading each
    // mound individually leaves a seam wherever two of them overlap, and they
    // are placed to overlap.
    const tiers = (b: Blob, x: number, inset: number) => {
      const t1 = Math.max(1, Math.floor(b.h * 0.4));
      const t2 = Math.max(2, Math.floor(b.h * 0.72));
      ctx.fillRect(x + inset, baseY - t1, b.w - inset * 2, t1);
      ctx.fillRect(x + 4 + inset, baseY - t2, b.w - 8 - inset * 2, t2);
      ctx.fillRect(x + 8 + inset, baseY - b.h, Math.max(2, b.w - 16 - inset * 2), b.h);
    };

    ctx.fillStyle = COLORS.bushDark;
    for (const b of this.bushes) {
      for (const wrap of [0, this.bushSpan]) {
        const x = Math.round(b.x - offset + wrap);
        if (x > this.artWidth || x + b.w < 0) continue;
        tiers(b, x, 0);
      }
    }
    ctx.fillStyle = COLORS.bush;
    for (const b of this.bushes) {
      for (const wrap of [0, this.bushSpan]) {
        const x = Math.round(b.x - offset + wrap);
        if (x > this.artWidth || x + b.w < 0) continue;
        tiers(b, x, 2);
      }
    }

    ctx.fillStyle = COLORS.bushEdge;
    ctx.fillRect(0, baseY - 2, this.artWidth, 2);
  }

  /**
   * The scrolling ground.
   *
   * Its top edge is exactly the death line: the bird dies when it touches the
   * grass, not somewhere below it. The strip is drawn under the play field, so
   * the visible ground costs the player no playable space.
   */
  drawGround(
    ctx: CanvasRenderingContext2D,
    simTimeMs: number,
    pipeSpeed: number,
    topY: number,
  ): void {
    const h = GROUND_ART_HEIGHT;
    ctx.fillStyle = COLORS.grass;
    ctx.fillRect(0, topY, this.artWidth, 5);
    ctx.fillStyle = COLORS.grassDark;
    ctx.fillRect(0, topY + 5, this.artWidth, 2);
    ctx.fillStyle = COLORS.sand;
    ctx.fillRect(0, topY + 7, this.artWidth, h - 7);

    // Diagonal stripes, scrolling at exactly pipe speed so the ground and the
    // pipes agree about how fast the world is moving.
    const period = 12;
    const offset = scrollOffset(simTimeMs, pipeSpeed, period);
    ctx.fillStyle = COLORS.sandDark;
    for (let x = -period; x < this.artWidth + period; x += period) {
      const sx = Math.round(x - offset);
      for (let row = 0; row < h - 9; row++) {
        ctx.fillRect(sx + row, topY + 8 + row, 5, 1);
      }
    }

    ctx.fillStyle = COLORS.soil;
    ctx.fillRect(0, topY + h - 2, this.artWidth, 2);
  }
}

/**
 * Scroll position of a layer at a given simulation time, wrapped to its period.
 *
 * `pipeSpeed` is in logical px/s; the caller works in art px, hence ART_SCALE
 * is folded in by the caller passing an already-scaled speed. Kept here so all
 * three layers wrap the same way and none of them can drift against the others.
 */
function scrollOffset(simTimeMs: number, speedArtPxPerSec: number, period: number): number {
  const travelled = (simTimeMs / 1000) * speedArtPxPerSec;
  return ((travelled % period) + period) % period;
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function mix(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}
